import { ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import ReactSelect from 'react-select';

import { Dictionary, EmailTypeDisplayNames, EmailTypeEnum, IInsertScheduledEmailRequest, IScheduledEmail, IMultiSelectOption, LanguagesAbbr, PASSCODE_LIST_TYPE, Channel, SessionTypesFeatures, Session, RegistrationStepType, RegFieldsEnum, IScheduledEmailSettings, Registration, SessionTypesEnum } from '../../../../../../../types/working-model';

// State management
import { createEmptyEmail } from '../../../../../../../store/utils/create-event';
import { createScheduledEmail, editScheduledEmail, setADropdownIsOpen, updateRegistrationSettings } from '../../../../../../../store/actions/admin/create-event';
import { getPasscodeLists } from '../../../../../../../store/actions/admin/passcode-lists';
import { useTypedSelector } from '../../../../../../../store/reducers/use-typed-selector';

// Components
import { COLORS, ICONS } from '../../../../../../general-ui/icon';
import { reactSelectStyles, TReactSelectOption } from '../../../../../../general-ui/react-select/react-select-styles';
import { showAlert } from '../../../../../../general-ui/alert/alert-service';
import { Tooltip } from '../../../../../../general-ui/tooltip/tooltip';
import BasicInput from '../../../../../../general-ui/text-input/basic-input';
import Datepicker from '../../../../../../general-ui/datepicker/datepicker';
import Icon from '../../../../../../general-ui/icon';
import ModalComponent from '../../../../../../general-ui/modal/modal';
import Switch from '../../../../../../general-ui/switch/switch';
import WaitingIndicator from '../../../../../../general-ui/waiting-indicator/waiting-indicator';

// Utils
import { BYPASS_EMAIL_INTERVALS, isInPast, isWithinNMinutesAfterNow, setDateOnTimestamp } from '../../../../../../../utils/date-utils';
import { OptionalComponent } from '../../../../../../../utils/optional-component';
import allLanguages from '../../../../../../../utils/translate-langs.json';
import usePrevious from '../../../../../../../utils/use-previous';

// OTHER
import { eventNameAndDescription, getEmailToSend, sessionNameAndDescription } from '../../../../scheduled-emails/create-email';
import { ParamsProps } from '../../../../../../live-event/live-event';
import { useTranslate } from '../../../../../../../i18n/useTranslationModules';

const NameFirstInSubject = [EmailTypeEnum.session_reminder, EmailTypeEnum.session_live, EmailTypeEnum.session_ended];

const scheduleEmailOptions: TReactSelectOption[] = [];
Object.entries(EmailTypeDisplayNames).forEach(([value, label]) => {
	if (value !== EmailTypeEnum.event_registration_email) {
		scheduleEmailOptions.push({ value, label });
	}
});

interface IScheduleEmailModalProps {
	open: boolean;
	setOpen: Dispatch<SetStateAction<boolean>>;
	initialData?: IScheduledEmail;
	onClose: () => void;
}

function ScheduleEmailModal(props: IScheduleEmailModalProps) {
	const { open, setOpen, onClose, initialData } = props;
	const activeChannel = useTypedSelector(state => state.AuthReducer.user?.active_channel);
	const channels = useTypedSelector(state => state.AuthReducer.channels);
	const passcodeLists = useTypedSelector(state => state.PasscodeListReducer.passcodeLists);
	const publishedUrl = useTypedSelector(state => state.CreateEventReducer.publishedUrl);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const { language } = useParams<ParamsProps>();
	const { t } = useTranslate(["email"]);


	const [fetchedLists, setFetchedLists] = useState(false);
	const [data, setData] = useState(initialData || createEmptyEmail(activeChannel ?? 0, workingEvent?.uuid ?? ''));
	const [submitting, setSubmitting] = useState(false);
	const [subject, setSubject] = useState(``); // Get default subject	
	const [firstData, setFirstData] = useState(data);

	useEffect(() => { 
		// Data from the first render. Used to set the subject
		setFirstData(initialData || createEmptyEmail(activeChannel ?? 0, workingEvent?.uuid ?? ''));
	}, [activeChannel, data.email_type, initialData, workingEvent?.uuid, open]);

	useEffect(() => {
		if (!workingEvent || !firstData ) return;

		const customizations = { ...(firstData?.working_customizations ?? firstData?.customizations) };
		const emailType = data.email_type ?? firstData.email_type;
		const session = firstData.session_uuid ? workingEvent.sessions.find(session => session.uuid === firstData?.session_uuid) : undefined;
		const { name } = session ? sessionNameAndDescription(session, language) : eventNameAndDescription(workingEvent, language);

		const defaultSubject = (NameFirstInSubject.includes(emailType) ?
			`${name} ${t(`subject.${emailType}`)}`
			: `${t(`subject.${emailType}`)} ${name}`);

		const isEventRegistrationEmail = data.email_type === EmailTypeEnum.event_registration_email;
		const registrationConfirmationEmailSubject = workingEvent.registration_settings?.registration_confirmation_email?.[language]?.subject;
		const customizationsSubject = customizations[language]?.subject;

		if (isEventRegistrationEmail && registrationConfirmationEmailSubject) {
			setSubject(registrationConfirmationEmailSubject);
		}
		else if (customizationsSubject) {
			setSubject(customizationsSubject);
		}
		else {
			setSubject(defaultSubject);
		}
	}, [firstData, language, t, workingEvent, data.email_type]);

	const currentChannel: Channel | undefined = useMemo(() => {
		return channels.find((channel) => channel.channel === activeChannel);
	}, [channels, activeChannel]);

	const isSessionEmailType = data.email_type.includes('session');
	const isRegistrationEmail = data.email_type === EmailTypeEnum.event_registration_email;
	const registrationIsOn = workingEvent?.registration_on;

	const errorCreatingSessionEmail = useMemo(() => {
		const generalStep = workingEvent?.registration_steps?.find(step => step.type === RegistrationStepType.general);
		const hasEmailField = generalStep?.questions?.find(question => question.registration_question === RegFieldsEnum.email);

		return isSessionEmailType && (!registrationIsOn || !hasEmailField);
	}, [isSessionEmailType, registrationIsOn, workingEvent?.registration_steps]);

	const dispatch = useDispatch();

	const prevChannel = usePrevious(activeChannel);
	// if passcode lists is not in redux, fetch them
	useEffect(() => {
		// fetchedLists is a fallback so we don't ever accidentally end up in an infinite loop
		if (!passcodeLists?.length && !fetchedLists || (prevChannel !== activeChannel)) {
			if (activeChannel && token) {
				setFetchedLists(true);
				dispatch(getPasscodeLists(activeChannel, token));
			}
		}
	}, [passcodeLists, activeChannel, token, dispatch, fetchedLists, prevChannel]);

	// FIELD HANDLERS
	const handleDeliveryDateFieldChange = ( value: Date) => {
		if (!value) return;
		return setData(prev => ({
			...prev,
			delivery_date: setDateOnTimestamp(data.delivery_date, value as unknown as string),
		}));
	};

	const handleDeliveryTimeFieldChange = ( value: number ) => {
		if (!value) return;
		return setData(prev => ({
			...prev,
			delivery_date: value,
		}));
	};


	
	const handleSubjectFieldChange = (value: string) => {
		const updatedCustomizations = {
			...data?.working_customizations,
			...data?.customizations,
			[language]: {
				...(data?.working_customizations?.[language] ?? data.customizations[language]),
				subject: value as string,
			}
		};
		return setData(prev => ({
			...prev,
			working_customizations: updatedCustomizations
		}));
	
	};

	const handleEmailFieldChange = (value: EmailTypeEnum) => {
		setData(prev => ({
			...prev,
			email_type: value,
		}));
	};

	const handleSessionUUIDFieldChange = (value: string | undefined) => {
		if (!value) return;
		setData(prev => ({
			...prev,
			session_uuid: value,
		}));
	};

	const handleAudienceFieldChange = (value: IMultiSelectOption[]) => {
		if (!value) return;
		setData(prev => ({
			...prev,
			audiences: value,
		}));
	};

	const handleTimezoneFieldChange = ( value: string) => {
		if (!value) return;
		setData(prev => ({
			...prev,
			timezone: value,
		}));
	};

	const handleLanguageFieldChange = (value: LanguagesAbbr) => {
		setData(prev => ({
			...prev,
			language: value,
		}));
	};

	const handleSubjectChange = (value: ChangeEvent<HTMLInputElement>) => {
		const target = value?.target as HTMLInputElement;
		setSubject(target?.value);
		handleSubjectFieldChange(target?.value);
	};

	const handleSettingsChange = (key: keyof IScheduledEmailSettings, value: boolean) => {
		setData(prev => ({
			...prev,
			settings: {
				...prev.settings,
				[key]: value,
			}
		}));
	};

	const handleSave = async () => {
		if (!token || !workingEvent || !currentChannel) return;
		if (tooLateToChange(data)) {
			return showAlert({
				description: 'Cannot edit schedules within 10 minutes from delivery time',
				duration: 7000,
				type: "error"
			});
		}
		setSubmitting(true);
		let errMsg = '';
		if (isSessionEmailType) {
			if (!data?.session_uuid) {
				errMsg = 'Session is required';
			} else if (!data.delivery_date) {
				errMsg = 'Delivery date is required';
			}
		} else if (!isRegistrationEmail) {
			if (!data?.audiences?.length) {
				errMsg = 'At least one audience is required';
			} else if (!data.delivery_date) {
				errMsg = 'Delivery date is required';
			}
		}
		if (!publishedUrl) {
			errMsg = 'Event must be published before scheduling an email';
		}
		if (errMsg || !publishedUrl) {
			setSubmitting(false);
			return showAlert({
				description: errMsg,
				duration: 7000,
				type: "error"
			});
		}

		const updatedCustomizations = {
			...(data?.working_customizations ?? data?.customizations),
			[language]: {
				...(data?.working_customizations?.[language] ?? data.customizations[language]),
				subject: subject,
			}
		};

		try {
			if (!user || !token) throw Error;
			// special handling for registration emails - only modifying settings & timezone in this modal
			if (isRegistrationEmail) {
				if (!workingEvent.registration_settings) return;
				const updatedRegistrationSettings: Registration = {
					...workingEvent.registration_settings,
					registration_email_settings: data.settings,
					registration_email_timezone: data.timezone,
					registration_confirmation_email: updatedCustomizations,
				};
				return dispatch(updateRegistrationSettings(token, workingEvent?.uuid, updatedRegistrationSettings));
			}

			// event emails are sent in a specific language but session emails go out in all event languages
			const emailLanguage = isSessionEmailType ? undefined : data.language ?? language;

			// if all sessions are on demand, we do not show any calendar actions
			let _settings = { ...data.settings };
			if (isAllOnDemand()) {
				_settings = {
					..._settings,
					showAddCalendar: false,
				};
			}

			const emailContent = await getEmailToSend(
				user,
				token,
				data.email_type,
				workingEvent,
				currentChannel,
				publishedUrl,
				data.working_customizations ?? data.customizations ?? {},
				emailLanguage,
				data.session_uuid,
				data.delivery_date,
				data.timezone || 'America/Los_Angeles',
				_settings,
			);
			// update/save audience invitation in event settings
			const saveableSchedule: IScheduledEmail | IInsertScheduledEmailRequest = {
				...data,
				settings: _settings,
				language: emailLanguage,
				audiences: isSessionEmailType ? [] : data.audiences ?? [],
				session_uuid: isSessionEmailType ? data.session_uuid : undefined,
				working_customizations: emailContent,
			};

			if (initialData) {
				dispatch(editScheduledEmail(token, saveableSchedule as IScheduledEmail));
			} else {
				dispatch(createScheduledEmail(token, saveableSchedule));
			}
		} catch {
			showAlert({
				description: 'Error creating email. Please try again',
				duration: 7000,
				type: "error"
			});
		} finally {
			handleClose();
			setSubmitting(false);
		}
	};

	const tooLateToChange = useCallback((data: IInsertScheduledEmailRequest) => {
		return !BYPASS_EMAIL_INTERVALS && isWithinNMinutesAfterNow(data.delivery_date, 10);
	}, []);

	const sessionOptions = useMemo(() => {
		return workingEvent?.sessions.map(session => ({ label: session.title[language] ?? session.title.base, value: session.uuid }));
	}, [language, workingEvent?.sessions]);

	// create a language map of all languages for faster lookup
	const langMap = useMemo(() => {
		const langMap: Record<string, string> = {};
		allLanguages.forEach(lang => {
			langMap[lang.code] = lang.label;
		});
		return langMap;
	}, []);

	const passcodeListOptions = passcodeLists.length
		? passcodeLists
			.filter(list => list.type !== PASSCODE_LIST_TYPE.PASSCODES_ONLY)
			.map(list => ({ label: list.name, value: list.uuid }))
		: undefined;

	// create a list of language options for all languages in sessions
	const languageOptions: () => { label: string; value: LanguagesAbbr; }[] = useCallback(() => {
		if (!workingEvent?.sessions?.length) return [];

		let options = Array.from(new Set(workingEvent.sessions.map(session => session.languages).flat()))
			.filter(lang => !!langMap[lang])
			.map(lang => ({
				label: langMap[lang],
				value: lang as LanguagesAbbr,
			}));

		if (!options?.length) {
			options = [{ label: 'English', value: 'en' as LanguagesAbbr }];
		}

		return options;
	}, [langMap, workingEvent?.sessions]);

	// get the first default session language
	const firstDefaultSessionLanguage: () => string = useCallback(() => {
		if (!workingEvent?.sessions?.length) return 'en';
		return workingEvent.sessions[0].default_language ?? 'en';
	}, [workingEvent?.sessions]);

	// create the default language option
	// if initial data then use that, else use the first default language, else english
	const defaultLanguageOption: () => { label: string; value: LanguagesAbbr; } = useCallback(() => {
		if (initialData?.language) {
			return {
				label: langMap[initialData.language],
				value: initialData.language,
			};
		}
		const option = languageOptions().find(lang => lang.value === firstDefaultSessionLanguage()) ?? languageOptions()[0];
		return option ?? { label: 'English', value: 'en' };

		// note: do not pass data as a dependency here or it will trigger the useEffect that resets state to empty values whenever a new language is selected
	}, [langMap, languageOptions, firstDefaultSessionLanguage, initialData]);

	// ensure we add or clear the data depending on whether the modal is open or not or we'll get stale data in local state
	useEffect(() => {
		if (initialData) {
			setData(initialData);
		}

		if (!initialData && workingEvent?.uuid && activeChannel) {
			setData(createEmptyEmail(activeChannel, workingEvent.uuid, EmailTypeEnum.event_invite, defaultLanguageOption().value));
		}
	}, [activeChannel, defaultLanguageOption, initialData, workingEvent?.uuid]);

	const handleClose = useCallback(() => {
		setOpen(false);
		setData(initialData || createEmptyEmail(activeChannel ?? 0, workingEvent?.uuid ?? '', EmailTypeEnum.event_invite, defaultLanguageOption().value));
		onClose();
	}, [activeChannel, defaultLanguageOption, initialData, onClose, setOpen, workingEvent?.uuid]);

	const sharedDropdownProps = reactSelectStyles({
		control: {
			borderRadius: '100px',
			overflow: 'hidden',
			border: '1px solid #908CA0',
			padding: '0px 16px',
		},
		menu: {
			borderRadius: '12px',

		},
		menuList: {
			borderRadius: '12px',
			padding: '0',
		},
		valueContainer: {
			maxHeight: '40vh',
			overflow: 'auto',
			fontSize: '14px',
			height: '48px',
			padding: '0',
		},
		singleValue: {
			marginLeft: '0',
		},
		dropdownIndicator: {
			color: "white"
		},
		clearIndicator: {
			color: "white"
		}
	});



	const sharedReactSelectProps = {
		classNamePrefix: "multi_select",
		className: "multi-select-component",
		tabSelectsValue: false,
		menuPortalTarget: document.body,
		styles: sharedDropdownProps,
		onMenuOpen: () => dispatch(setADropdownIsOpen(true)),
		onMenuClose: () => setTimeout(() => dispatch(setADropdownIsOpen(false)), 100)
	};

	const timezoneOptions = useMemo(() => {
		const tzs = (Intl as any)?.supportedValuesOf?.('timeZone') ?? [];
		return tzs.map((timezone: string) => ({
			label: timezone,
			value: timezone,
		}));
	}, []);

	const emailHasStartTime = useCallback(() => {
		const sessionsWithStartTime: Record<string, Session> = {};
		workingEvent?.sessions.forEach(session => {
			const hasStartTime = !!SessionTypesFeatures?.[session?.session_type]?.has_start_time;
			if (hasStartTime) {
				sessionsWithStartTime[session.uuid] = session;
			}
		});

		{/* if event type email only display timezones if there's a session with a time */ }
		if (!isSessionEmailType) {
			if (Object.keys(sessionsWithStartTime).length) {
				return true;
			}
			return false;
		}

		/* if session type email, only display if selected session has time */
		if (data.session_uuid && sessionsWithStartTime[data.session_uuid]) {
			return true;
		}
		return false;
	}, [data.session_uuid, isSessionEmailType, workingEvent?.sessions]);

	// don't show calendar invites for emails going out after event/session starts
	const isNotInFuture = [EmailTypeEnum.session_live, EmailTypeEnum.session_ended].includes(data?.email_type);

	const workingEventSessions = workingEvent?.sessions;

	const isAllOnDemand = useCallback(() => {
		return !workingEventSessions?.find(session => session.session_type !== SessionTypesEnum.onDemand);
	}, [workingEventSessions]);

	const isOnDemandSession = useCallback(() => {
		return !!(
			data.session_uuid
			&& workingEventSessions?.find(s => s.uuid === data.session_uuid)?.session_type === SessionTypesEnum.onDemand
		);
	}, [data.session_uuid, workingEventSessions]);

	const initialSessioCalendarSetting = useCallback(() => {
		if (!data.settings || isOnDemandSession()) {
			return false;
		}
		if (data.settings.showAddCalendar) {
			return data.settings.showAddCalendar;
		}
		return true;
	}, [data.settings, isOnDemandSession]);

	const hideAddCalendar = !emailHasStartTime() || isNotInFuture || isOnDemandSession() || isAllOnDemand();

	if (!fetchedLists) return <WaitingIndicator />;
	return (
		<>
			<ModalComponent
				open={open}
				onRequestClose={handleClose}
				cancellable
				closeable={false}
				title={isRegistrationEmail ? "Registration Confirmation Email" : "Schedule Email"}
				noCloseOnEscape
				className="schedule-email-modal-body"
				padTitle={false}
				footer={
					<>
						<button onClick={handleClose}>Cancel</button>
						<button
							onClick={handleSave}
							className="primary"
							disabled={
								submitting
								|| !data.delivery_date
								|| (data.delivery_date && tooLateToChange(data))
								|| isInPast(data.delivery_date)
								|| errorCreatingSessionEmail
							}
						>Save</button>
					</>
				}
			>
				{/* start components excluded from registration email modal */}
				
				{/* SUBJECT */}
				<BasicInput
					defaultText={subject}
					label={'Email Subject *' }
					onChange={handleSubjectChange}
					value={subject}
					required={ true }
				/>
				
				<OptionalComponent display={!isRegistrationEmail}>
					<>
						<div className="full-width-dropdown">
							<div className="label-container">
								<label>Schedule Email Type *</label>
								<OptionalComponent display={errorCreatingSessionEmail}>
									<Tooltip tooltip={`This email is only sent to registered attendees with access to this session, please ${(!registrationIsOn) ? 'turn on registration' : 'add the email field to registration'} to use this email type.`} position="bottom-right">
										<Icon name={ICONS.PRIMARY_TOOLTIP} size={12} color={COLORS.CYAN} />
									</Tooltip>
								</OptionalComponent>
							</div>
							<ReactSelect
								defaultValue={{ label: EmailTypeDisplayNames[initialData?.email_type ?? EmailTypeEnum.event_invite], value: initialData?.email_type ?? EmailTypeEnum.event_invite }}
								options={scheduleEmailOptions}
								onChange={value => {
									value && handleEmailFieldChange(value.value as EmailTypeEnum);
								}}
								closeMenuOnSelect
								{...sharedReactSelectProps}
							/>
						</div>

						{/* TYPE SPECIFIC */}
						<OptionalComponent display={isSessionEmailType}>
							<div className="full-width-dropdown">
								<label>Session *</label>
								<ReactSelect
									options={sessionOptions}
									onChange={value => handleSessionUUIDFieldChange(value?.value)}
									defaultValue={data?.session_uuid ? sessionOptions?.find(option => option.value === data?.session_uuid) : undefined}
									value={data.session_uuid ? sessionOptions?.find(option => option.value === data.session_uuid) : undefined}
									placeholder={''}
									closeMenuOnSelect
									{...sharedReactSelectProps as Dictionary}
								/>
							</div>
						</OptionalComponent>

						<OptionalComponent display={!isSessionEmailType}>
							<div className="full-width-dropdown">
								<div className="label-container">
									<label>Audience List *</label>
									<OptionalComponent display={!isSessionEmailType}>
										<Tooltip tooltip="Audience lists must include an email field to be used here." position="bottom-right">
											<Icon name={ICONS.PRIMARY_TOOLTIP} size={12} color={COLORS.CYAN} />
										</Tooltip>
									</OptionalComponent>
								</div>
								<ReactSelect
									isMulti={true}
									defaultValue={data.audiences?.length ? data.audiences : null}
									options={passcodeListOptions}
									onChange={(values) => handleAudienceFieldChange(values as IMultiSelectOption[])}
									value={data.audiences?.length ? data.audiences : null}
									closeMenuOnSelect={false}
									{...sharedReactSelectProps}
								/>
							</div>
						</OptionalComponent>

						{/* SHARED */}
						<div className="broadcast-date schedule-time-pickers">
							<div>
								<Datepicker
									label="Date *"
									// minDate={minDate}
									onChange={(date) => {
										handleDeliveryDateFieldChange(date);
									}}
									placeholder="Select date"
									portalId="date-portal"
									datepickerProps={{
										showTimeSelect: false,
										shouldCloseOnSelect: true,
										dateFormat: 'MM/dd/yyyy',
										calendarClassName: 'time-picker',
									}}
									value={new Date(data.delivery_date || Date.now())}
								/>
							</div>

							<div className="time-picker-input-container time-selector">
								<Datepicker
									label="Time *"
									onChange={updatedDate => {
										// since we can type into this input, this keeps it so that you can only modify the delivery_date time of day
										const currentDeliveryDate = new Date(data.delivery_date);
										const isSameYear = updatedDate.getFullYear() == currentDeliveryDate.getFullYear();
										const isSameMonth = updatedDate.getMonth() == currentDeliveryDate.getMonth();
										const isSameDay = updatedDate.getDate() == currentDeliveryDate.getDate();

										if (isSameYear && isSameMonth && isSameDay) {
											handleDeliveryTimeFieldChange(updatedDate.getTime());
										}
									}}
									onBlur={() => {
										if (BYPASS_EMAIL_INTERVALS) return;

										const fiveMinuteIncrement = 1000 * 60 * 5;
										if (data.delivery_date % fiveMinuteIncrement !== 0) {
											const roundedUpToNearest5MinuteIncrement = new Date(Math.ceil(data.delivery_date / fiveMinuteIncrement) * fiveMinuteIncrement);
											handleDeliveryTimeFieldChange(roundedUpToNearest5MinuteIncrement.getTime());
										}
									}}
									portalId="time-portal"
									required
									popperPlacement="bottom"
									icon={ICONS.CLOCK}
									value={new Date(data?.delivery_date ? data.delivery_date : '')}
									datepickerProps={{
										timeCaption: "Time",
										dateFormat: "h:mm aa",
										shouldCloseOnSelect: true,
										calendarClassName: 'time-picker',
									}}
								/>
							</div>

							<OptionalComponent display={!isSessionEmailType}>
								<div className="invite-language-selector">
									<label>Language</label>
									<ReactSelect
										tabSelectsValue={false}
										defaultValue={languageOptions().find(option => option.value === data.language)}
										options={languageOptions()}
										menuPortalTarget={document.body}
										onChange={(value) => {
											handleLanguageFieldChange((value?.value ?? 'en') as LanguagesAbbr );
										}}
										placeholder={''}
										styles={sharedDropdownProps}
										onMenuOpen={() => dispatch(setADropdownIsOpen(true))}
										onMenuClose={() => setTimeout(() => dispatch(setADropdownIsOpen(false)), 100)}
									/>
								</div>
							</OptionalComponent>
						</div>

						<OptionalComponent display={emailHasStartTime()}>
							<div className="border" >
								<h6>Display Settings</h6>
							</div>
						</OptionalComponent>

					</>
				</OptionalComponent>
				{/* end components excluded from registration email modal */}

				<OptionalComponent display={!hideAddCalendar}>
					<div className="email-modal-switch">
						<span>Display Add To Calendar options</span>
						<Switch
							value={'showAddCalendar'}
							on={!!data.settings?.showAddCalendar ?? initialSessioCalendarSetting()}
							onClick={(v, on) => handleSettingsChange(v, on)}
							inline
						/>
					</div>
				</OptionalComponent>

				<OptionalComponent display={emailHasStartTime()}>
					<div className="email-modal-switch">
						<span>Display {isSessionEmailType ? "session" : "event"} date/time in email</span>
						<Switch
							value={'showStartTime'}
							on={!!data.settings?.showStartTime}
							onClick={(v, on) => handleSettingsChange(v, on)}
							inline
						/>
					</div>
				</OptionalComponent>

				<OptionalComponent display={emailHasStartTime() && !!data.settings?.showStartTime}>
					<div className="email-timezone-picker-container">
						<label>Display date/time in selected timezone</label>
						<ReactSelect
							options={timezoneOptions}
							defaultValue={{ label: data?.timezone || 'America/Los_Angeles', value: data?.timezone || 'America/Los_Angeles' }}
							{...sharedReactSelectProps}
							onChange={(value) => {
								const selectedTimezone = value?.value || 'America/Los_Angeles';
								handleTimezoneFieldChange(selectedTimezone);
							}}
							styles={sharedDropdownProps}
						/>
					</div>
				</OptionalComponent>

				{/* errors */}
				<div>
					<OptionalComponent display={!initialData && tooLateToChange(data)}>
						<p className='invalid-form-text'>Cannot save schedule within 10 minutes of delivery time</p>
					</OptionalComponent>
					<OptionalComponent display={initialData && tooLateToChange(data)}>
						<p className='invalid-form-text'>Cannot edit schedules within 10 minutes from delivery time</p>
					</OptionalComponent>
					<OptionalComponent display={!!(data.delivery_date && isInPast(data.delivery_date))}>
						<p className='invalid-form-text'>Schedule time cannot be in the past</p>
					</OptionalComponent>
					<OptionalComponent display={errorCreatingSessionEmail}>
						<p className='invalid-form-text'>{!registrationIsOn ? "Registration must be on" : "Add the email field to registration"} to send session emails.</p>
					</OptionalComponent>
				</div>
			</ModalComponent>
		</>
	);
}

export default ScheduleEmailModal;
